import { inferRegionalLangFromTimeZone } from './timezoneLocale';

/** Legacy mirror — kept for OwnerSettingsPanel and older bookmarks */
export const TCARDS_LANG_STORAGE_KEY = 'tcards_lang';

/** Explicit choice from language dropdown / settings (like mazuu_public_language) */
export const TCARDS_LANG_MANUAL_KEY = 'tcards_lang_manual';

/** Geo / timezone / browser sw|fr auto path (like mazuu_public_language_auto) */
export const TCARDS_LANG_AUTO_KEY = 'tcards_lang_auto';

export const SUPPORTED_UI_LANGS = ['en', 'sw', 'fr'] as const;
export type SupportedUiLang = (typeof SUPPORTED_UI_LANGS)[number];

export function normalizeUiLang(lng: string | undefined | null): SupportedUiLang {
  const base = String(lng || 'en').split('-')[0].toLowerCase();
  return (SUPPORTED_UI_LANGS as readonly string[]).includes(base) ? (base as SupportedUiLang) : 'en';
}

/** @deprecated Prefer persistManualLanguage / persistAutoLanguage */
export function persistLanguagePreference(lng: string): void {
  persistManualLanguage(lng);
}

export function persistManualLanguage(lng: string): void {
  const n = normalizeUiLang(lng);
  try {
    localStorage.setItem(TCARDS_LANG_MANUAL_KEY, n);
    localStorage.setItem(TCARDS_LANG_STORAGE_KEY, n);
    localStorage.removeItem(TCARDS_LANG_AUTO_KEY);
  } catch {
    /* ignore */
  }
}

export function persistAutoLanguage(lng: string): void {
  if (readManualLanguage()) return;
  const n = normalizeUiLang(lng);
  try {
    localStorage.setItem(TCARDS_LANG_AUTO_KEY, n);
    localStorage.setItem(TCARDS_LANG_STORAGE_KEY, n);
  } catch {
    /* ignore */
  }
}

export function readManualLanguage(): string | null {
  try {
    return localStorage.getItem(TCARDS_LANG_MANUAL_KEY);
  } catch {
    return null;
  }
}

export function readAutoLanguage(): string | null {
  try {
    return localStorage.getItem(TCARDS_LANG_AUTO_KEY);
  } catch {
    return null;
  }
}

export function readStoredLanguagePreference(): string | null {
  try {
    return localStorage.getItem(TCARDS_LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Resolved public UI language for panels that read localStorage directly */
export function readEffectivePublicLanguage(): SupportedUiLang {
  return normalizeUiLang(
    readManualLanguage() || readAutoLanguage() || readStoredLanguagePreference() || 'en'
  );
}

/**
 * Remove stale auto/legacy English when device timezone indicates Kiswahili primary regions.
 * Prevents TZ users with en-US phones from being stuck on a previously persisted `en`.
 */
export function clearStaleEnglishForEastAfricaBrowser(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(TCARDS_LANG_MANUAL_KEY)) return;
    if (inferRegionalLangFromTimeZone() !== 'sw') return;

    if (localStorage.getItem(TCARDS_LANG_AUTO_KEY) === 'en') {
      localStorage.removeItem(TCARDS_LANG_AUTO_KEY);
    }
    if (localStorage.getItem(TCARDS_LANG_STORAGE_KEY) === 'en') {
      localStorage.removeItem(TCARDS_LANG_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}
